import React, { useState } from 'react';
import {
  Typography,
  Paper,
  CircularProgress,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  ArrowBackIos,
  ArrowForwardIos,
  Receipt,
} from '@mui/icons-material';
import {
  FiTrendingUp,
  FiTrendingDown,
  FiDollarSign,
  FiFileText,
  FiCheckCircle,
  FiClock,
} from 'react-icons/fi';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

import styles from './Relatorios.module.css';
import { useTransactionData } from '../../../hooks/useTransactionData';
import { PieChart, BarChart } from '../../Charts';
import { StatCard } from '../../StatCard';
import {
  processDataByCategory,
  processDataByUser,
  processDataByPaymentStatus,
  processIncomeVsExpense,
  processTopExpenseCategories,
  calculateSummaryStats,
} from '../../../utils/chartUtils';

dayjs.locale('pt-br');

export function Relatorios() {
  const [currentDate, setCurrentDate] = useState(dayjs());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 768px
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm')); // 600px
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('xl')); // 1200px+
  
  const { transactions, loading, error } = useTransactionData(
    currentDate.month() + 1,
    currentDate.year()
  );

  const handlePreviousMonth = () => {
    setCurrentDate(prev => prev.subtract(1, 'month'));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => prev.add(1, 'month'));
  };

  const handleYearChange = (year: number) => {
    setCurrentDate(prev => prev.year(year));
  };

  const handleMonthChange = (month: number) => {
    setCurrentDate(prev => prev.month(month));
  };

  // Altura dos gráficos responsiva
  const chartHeight = isSmallMobile ? 200 : isMobile ? 250 : isLargeScreen ? 400 : 350;
  
  // Processar dados para os gráficos
  const expensesByCategory = processDataByCategory(transactions, 'Saída');
  const incomesByCategory = processDataByCategory(transactions, 'Entrada');
  const dataByUser = processDataByUser(transactions);
  const paymentStatusData = processDataByPaymentStatus(transactions);
  const incomeVsExpenseData = processIncomeVsExpense(transactions);
  const topExpensesData = processTopExpenseCategories(transactions, 5);
  const summaryStats = calculateSummaryStats(transactions);

  // Anos disponíveis (últimos 5 anos + próximos 2)
  const currentYear = dayjs().year();
  const availableYears = Array.from(
    { length: 8 },
    (_, i) => currentYear - 3 + i
  );

  // Meses
  const months = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
  ];

  if (loading) {
    return (
      <main className={styles.main}>
        <div className={styles.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginTop: 16 }}>
            Carregando relatórios...
          </Typography>
        </div>
      </main>
    );
  }

  if (error) {
    return (
      <main className={styles.main}>
        <div className={styles.errorContainer}>
          <Alert severity="error" style={{ marginBottom: 16 }}>
            {error}
          </Alert>
          <Typography variant="h6">
            Erro ao carregar os dados dos relatórios
          </Typography>
        </div>
      </main>
    );
  }

  if (transactions.length === 0) {
    return (
      <main className={styles.main}>
        <div className={styles.header}>
          <Typography variant="h4" className={styles.title}>
            Relatórios Financeiros
          </Typography>
          <div className={styles.dateSelector}>
            <FormControl size="small" style={{ minWidth: 120 }}>
              <InputLabel>Mês</InputLabel>
              <Select
                value={currentDate.month()}
                label="Mês"
                onChange={(e) => handleMonthChange(e.target.value as number)}
              >
                {months.map((month, index) => (
                  <MenuItem key={index} value={index}>
                    {month}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" style={{ minWidth: 100 }}>
              <InputLabel>Ano</InputLabel>
              <Select
                value={currentDate.year()}
                label="Ano"
                onChange={(e) => handleYearChange(e.target.value as number)}
              >
                {availableYears.map((year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        </div>
        <div className={styles.noDataContainer}>
          <Receipt style={{ fontSize: 80, color: '#ccc', marginBottom: 16 }} />
          <Typography variant="h6">
            Nenhuma transação encontrada para {currentDate.format('MMMM [de] YYYY')}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Adicione algumas transações para visualizar os relatórios
          </Typography>
        </div>
      </main>
    );
  }

  return (
    <main className={styles.main}>
      {/* Header */}
      <div className={styles.header}>
        <Typography variant="h4" className={styles.title}>
          Relatórios Financeiros
        </Typography>
        <div className={styles.dateSelector}>
          <IconButton onClick={handlePreviousMonth} size="small">
            <ArrowBackIos />
          </IconButton>
          <FormControl size="small" style={{ minWidth: 120 }}>
            <InputLabel>Mês</InputLabel>
            <Select
              value={currentDate.month()}
              label="Mês"
              onChange={(e) => handleMonthChange(e.target.value as number)}
            >
              {months.map((month, index) => (
                <MenuItem key={index} value={index}>
                  {month}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" style={{ minWidth: 100 }}>
            <InputLabel>Ano</InputLabel>
            <Select
              value={currentDate.year()}
              label="Ano"
              onChange={(e) => handleYearChange(e.target.value as number)}
            >
              {availableYears.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <IconButton onClick={handleNextMonth} size="small">
            <ArrowForwardIos />
          </IconButton>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className={styles.statsGrid}>
        <StatCard
          title="Total de Entradas"
          value={summaryStats.totalEntrada}
          subtitle={`${summaryStats.countEntrada} transações`}
          color="success"
          icon={<FiTrendingUp />}
        />
        <StatCard
          title="Total de Saídas"
          value={summaryStats.totalSaida}
          subtitle={`${summaryStats.countSaida} transações`}
          color="danger"
          icon={<FiTrendingDown />}
        />
        <StatCard
          title="Saldo do Mês"
          value={summaryStats.saldo}
          subtitle={summaryStats.saldo >= 0 ? 'Positivo' : 'Negativo'}
          color={summaryStats.saldo >= 0 ? 'success' : 'danger'}
          icon={<FiDollarSign />}
        />
        <StatCard
          title="Total de Transações"
          value={summaryStats.totalTransacoes.toString()}
          subtitle={`${summaryStats.countPago} pagas, ${summaryStats.countPendente} pendentes`}
          color="info"
          icon={<FiFileText />}
        />
        <StatCard
          title="Valores Pagos"
          value={summaryStats.totalPago}
          subtitle={`${summaryStats.countPago} transações`}
          color="success"
          icon={<FiCheckCircle />}
        />
        <StatCard
          title="Valores Pendentes"
          value={summaryStats.totalPendente}
          subtitle={`${summaryStats.countPendente} transações`}
          color="warning"
          icon={<FiClock />}
        />
      </div>

      {/* Gráficos */}
      <div className={styles.chartsGrid}>
        {/* Entradas vs Saídas */}
        <Paper className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>
            Entradas vs Saídas
          </Typography>
          <PieChart
            data={incomeVsExpenseData}
            height={chartHeight}
          />
        </Paper>

        {/* Gastos por Categoria */}
        <Paper className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>
            Gastos por Categoria
          </Typography>
          <PieChart
            data={expensesByCategory}
            height={chartHeight}
          />
        </Paper>

        {/* Receitas por Categoria */}
        <Paper className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>
            Receitas por Categoria
          </Typography>
          {incomesByCategory.datasets[0].data.length > 0 ? (
            <PieChart
              data={incomesByCategory}
              height={chartHeight}
            />
          ) : (
            <div style={{ 
              height: chartHeight, 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              color: '#666',
              fontSize: isMobile ? '1rem' : '1.1rem'
            }}>
              Nenhuma receita encontrada para este período
            </div>
          )}
        </Paper>

        {/* Status de Pagamento */}
        <Paper className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>
            Status de Pagamento
          </Typography>
          <PieChart
            data={paymentStatusData}
            height={chartHeight}
          />
        </Paper>

        {/* Transações por Usuário */}
        <Paper className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>
            Transações por Usuário
          </Typography>
          <BarChart
            data={dataByUser}
            height={chartHeight}
          />
        </Paper>

        {/* Top 5 Categorias de Gastos */}
        <Paper className={styles.chartCard}>
          <Typography variant="h6" className={styles.chartTitle}>
            Top 5 Categorias de Gastos
          </Typography>
          <BarChart
            data={topExpensesData}
            height={isSmallMobile ? chartHeight + 30 : isMobile ? chartHeight + 50 : isLargeScreen ? chartHeight + 100 : chartHeight + 50}
            horizontal={true}
          />
        </Paper>
      </div>
    </main>
  );
}